import {
  _addItemToCart,
  _getCartItems,
  _removeItemFromCart, // _addCustomerToCart,
  _sendCreateCart,
  _updateCartIdentity,
} from '@src/cart/cartApiQueries';
import localStorageGateway from '@src/services/localStorageGateway';
import teachersRepository from '@src/teachers/teachersRepository';
import { jsonParse } from '@src/utils/jsonParse';
import { t } from 'i18next';
import { action, computed, makeObservable, observable, reaction } from 'mobx';
import { productTypes } from './cartProductTypes';

class CartRepository {
  locale = 'EN';
  isModalOpen = false;
  checkoutUrl = null;
  allLinesInCart = [];
  estimatedCost = {};
  win = null;

  constructor() {
    makeObservable(this, {
      locale: observable,
      updateLocale: action,
    });

    makeObservable(this, {
      checkoutUrl: observable,
      updateCheckoutUrl: action,
      getCheckoutUrl: computed,
    });

    makeObservable(this, {
      isModalOpen: observable,
      toggleModal: action,
    });

    makeObservable(this, {
      allLinesInCart: observable,
      lessonItems: computed,
      materialItems: computed,
      seminarItems: computed,
      updateLinesInCart: action,
    });

    makeObservable(this, {
      estimatedCost: observable,
      subtotalAmount: computed,
      totalAmount: computed,
      updateEstimatedCost: action,
    });
  }

  get materialItems() {
    return this.allLinesInCart.filter((line) => {
      return (
        line.productType === productTypes.material.shopifyProductType ||
        line.productType === productTypes.material.shopifyProductTypeRo
      );
    });
  }

  get lessonItems() {
    return this.allLinesInCart.filter((line) => {
      return (
        line.productType === productTypes.lesson.shopifyProductType ||
        line.productType === productTypes.lesson.shopifyProductTypeRo
      );
    });
  }

  get seminarItems() {
    return this.allLinesInCart.filter((line) => {
      return (
        line.productType === productTypes.seminar.shopifyProductType ||
        line.productType === productTypes.seminar.shopifyProductTypeRo
      );
    });
  }

  get subtotalAmount() {
    return this.estimatedCost.subtotalAmount;
  }

  get totalAmount() {
    return this.estimatedCost.totalAmount;
  }

  get cartCurrency() {
    return this.estimatedCost.currency;
  }

  get getCheckoutUrl() {
    return this.checkoutUrl;
  }

  load = (callback, locale) => {
    reaction(
      () => this.isModalOpen,
      (value) => callback(value),
      { fireImmediately: true }
    );

    this.updateLocale(locale);
  };

  getLines = async (callback, user) => {
    reaction(
      () => this.allLinesInCart.map((item) => item),
      (value) => callback(value),
      { fireImmediately: true }
    );

    await this.loadCartData(user);
  };

  toggleModal = () => {
    this.isModalOpen = !this.isModalOpen;
  };

  loadCartData = async (user) => {
    const cartId = localStorageGateway.get(`shopifyCartId:${user?.email}`);

    if (!cartId) {
      return;
    }

    const locale = this.locale;
    const resDto = await _getCartItems({ cartId, locale });

    if (resDto?.errors?.length > 0) {
      // There was api error while loading cart items
      return;
    }

    // cart was bought, clearing cart data
    if (resDto.data.cart === null) {
      localStorageGateway.set(`shopifyCartId:${user?.email}`, null, 86400000); //TTL: 24h
      return;
    }

    let cartCurrency;

    const allLinesPm = resDto.data.cart.lines.edges.map((lineDto) => {
      cartCurrency = lineDto.node.merchandise.priceV2.currencyCode;
      const attributes = {};
      lineDto.node.attributes.forEach((attr) => {
        attributes[attr.key] = attr.value;
      });

      return {
        id: lineDto.node.id,
        locale: this.locale,
        quantity: lineDto.node.quantity,
        title: lineDto.node.merchandise.title,
        currency: lineDto.node.merchandise.priceV2.currencyCode,
        subtotalAmount: lineDto.node.estimatedCost.subtotalAmount.amount,
        totalAmount: lineDto.node.estimatedCost.totalAmount.amount,
        variantId: lineDto.node.merchandise.id,
        productId: lineDto.node.merchandise.product.id,
        productType: lineDto.node.merchandise.product.productType,
        productTitle: lineDto.node.merchandise.product.title,
        productFeaturedImageUrl:
          lineDto.node.merchandise.product.featuredImage?.url,
        price: lineDto.node.merchandise.priceV2.amount,
        vendor: lineDto.node.merchandise.product.vendor,
        subjects: jsonParse(
          lineDto.node.merchandise.product.subject?.value,
          []
        ),
        formats: jsonParse(lineDto.node.merchandise.product.format?.value, []),
        day: attributes[t('dayKey')],
        time: attributes[t('timeKey')],
        topic: attributes[[t('topicKey')]],
        grade: attributes[[t('gradeKey')]],
        forUser: attributes[[t('forUserKey')]],
      };
    });

    const lessonLines = allLinesPm.filter(
      (line) => line.productType === 'Pamokos'
    );

    let teachersData = [];
    lessonLines.forEach((line) => {
      teachersData.push(
        teachersRepository.getTeacherDetailsInfoById(line.vendor)
      );
    });

    const resolved = await Promise.all(teachersData);
    resolved.forEach((teacher) => {
      const lines = allLinesPm.filter((line) => line.vendor === teacher.id);
      lines.forEach((line) => {
        line.teacherName = teacher.name;
      });
    });

    const estimatedCost = {
      totalAmount: resDto.data.cart.estimatedCost.totalAmount.amount,
      subtotalAmount: resDto.data.cart.estimatedCost.subtotalAmount.amount,
      currency: cartCurrency,
    };

    const checkoutUrl = resDto.data.cart.checkoutUrl;

    this.updateEstimatedCost(estimatedCost);
    this.updateLinesInCart(allLinesPm);
    this.updateCheckoutUrl(checkoutUrl);
  };

  addItemToCart = async ({
    variantId,
    topic,
    selectedTime,
    user,
    subject,
    grade,
    forUser,
  }) => {
    let cartId = localStorageGateway.get(`shopifyCartId:${user?.email}`);

    if (!cartId) {
      const newCartDto = await _sendCreateCart();
      cartId = newCartDto.data.cartCreate.cart.id;
      if (cartId) {
        localStorageGateway.set(
          `shopifyCartId:${user?.email}`,
          cartId,
          86400000
        ); //TTL: 24h
      }
    }

    if (!forUser) {
      forUser = '';
    }

    const resDto = await _addItemToCart({
      cartId,
      variantId,
      topic,
      selectedTime,
      subject,
      grade,
      forUser,
      byUser: user?.id,
      topicKey: t('topicKey'),
      timeKey: t('timeKey'),
      dayKey: t('dayKey'),
      subjectKey: t('subjectKey'),
      gradeKey: t('gradeKey'),
      forUserKey: t('forUserKey'),
      byUserKey: t('byUserKey'),
    });

    if (resDto?.errors?.length > 0) {
      // There was api error while loading cart items
      return;
    }

    await this.loadCartData(user);
  };

  removeItemFromCart = async ({ lineId, user }) => {
    const cartId = localStorageGateway.get(`shopifyCartId:${user?.email}`);

    const resDto = await _removeItemFromCart({ cartId, lineId });

    if (resDto?.errors?.length > 0) {
      // There was api error while trying to remove item
      return;
    }

    await this.loadCartData(user);
  };

  updateLinesInCart = (newLines) => {
    this.allLinesInCart = newLines;
  };

  updateEstimatedCost = (newEstimate) => {
    this.estimatedCost = newEstimate;
  };

  updateCheckoutUrl = (newUrl) => {
    this.checkoutUrl = newUrl;
  };

  updateLocale = (newLocale) => {
    if (newLocale) {
      this.locale = newLocale.toUpperCase();
    }
  };

  openWindow = () => {
    this.win = window.open();
  };

  openCheckout = async (user, loadingCallback) => {
    const checkoutUrl = this.getCheckoutUrl;

    const response = await fetch(
      process.env.GATSBY_API_URL + 'auth/shopifyToken',
      {
        headers: {
          ApiKey: process.env.GATSBY_API_KEY,
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user?.userToken}`,
        },
      }
    );

    const data = await response.json();

    const cartId = localStorageGateway.get(`shopifyCartId:${user?.email}`);
    await _updateCartIdentity({ cartId, token: data.token });

    window.location.href = checkoutUrl;

    if (loadingCallback) {
      loadingCallback(false);
    }
  };
}

const cartRepository = new CartRepository();

export default cartRepository;
